import { useMutation } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'

import { AnswerType, BookingAnswer, BookingQuestion, SelectType } from './booking-types.types'
import { useInvalidateBookingQuestions } from './get-booking-questions'

export interface AnswerPayload {
  order: number
  placeholder: string | null
  pricingType: BookingAnswer['pricingType']
  pricingValue: string | null
  title: string
  type: AnswerType
}

export interface QuestionPayload {
  imageLimit: number | null
  isRequired: boolean
  modal: string
  order: number
  selectType: SelectType
  title: string
}

export interface SaveBookingQuestionRequest {
  answers: {
    create: AnswerPayload[]
    deleteIds: number[]
    update: (AnswerPayload & { id: number })[]
  }
  bookingTypeId: number
  question: QuestionPayload
  questionId?: number
}

// Note: BE hangs on POST /question without an answers array
export const saveBookingQuestion = async ({
  answers,
  bookingTypeId,
  question,
  questionId,
}: SaveBookingQuestionRequest) => {
  if (questionId === undefined) {
    const created = await authAPI.post<BookingQuestion>('/question', {
      ...question,
      answers: answers.create,
      bookingTypeId,
    })
    return created.data
  }

  const updated = await authAPI.put<BookingQuestion>(`/question/${questionId}`, question)

  await Promise.all([
    ...answers.deleteIds.map((answerId) =>
      authAPI.delete(`/question/${questionId}/answer/${answerId}`),
    ),
    ...answers.update.map(({ id, ...answer }) =>
      authAPI.put(`/question/${questionId}/answer/${id}`, answer),
    ),
    ...answers.create.map((answer) => authAPI.post(`/question/${questionId}/answer`, answer)),
  ])

  return updated.data
}

export const deleteBookingQuestion = async (id: number) => {
  await authAPI.delete(`/question/${id}`)
}

export const useSaveBookingQuestion = () => {
  const invalidate = useInvalidateBookingQuestions()

  return useMutation({
    mutationFn: saveBookingQuestion,
    onSettled: invalidate,
  })
}

export const useDeleteBookingQuestion = () => {
  const invalidate = useInvalidateBookingQuestions()

  return useMutation({
    mutationFn: deleteBookingQuestion,
    onSuccess: invalidate,
  })
}
